import { Box, Button, Container, Stack, Typography, alpha, styled } from "@mui/material";
import {
  PublicRounded as PublicRoundedIcon,
} from "@mui/icons-material"
import StyleIcon from '@mui/icons-material/Style';
import PreferencesDialog from "./PreferencesDialog";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useAppSelector } from "../Common/_hooks/useAppSelector";
import HeaderLink from "./HeaderNavigation";
import { getHelpCenterUrl } from "../Common/helpCenter";

const NavText = styled('span')(({ theme }) => ({
  borderBottom: '4px solid transparent',
  paddingTop: '4px',
  color: '#444',
  lineHeight: 1,
  textDecoration: 'none',
}))

const Subnav = styled('div')(({theme}) => ({
  backgroundColor: '#fff',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderLeft: 0,
  borderRight: 0,
  boxShadow: '0 18px 28px rgba(0, 0, 0, 0.14)',
  clipPath: 'inset(0 -100px -100px -100px)',
  position: 'absolute',
  top: '52px',
  left: 0,
  zIndex: 1000,
  display: 'none',
  cursor: 'default',
  width: '100vw',
  maxWidth: '1280px',
}))

const SubnavBody = styled(Stack)(({theme}) => ({
  padding: theme.spacing(3),
  backgroundColor: '#fff',
}))

const SubnavSection = styled('div')(({theme}) => ({
  
}))

const SubnavUl = styled('ul')(({ theme }) => ({
  padding: 0,
  margin: 0,
}))

const SubnavLi = styled('li')(({ theme }) => ({
  '> a': {
    display: 'block',
    fontSize: '.9rem',
    color: theme.palette.secondary.dark,
    textDecoration: 'none',
    padding: theme.spacing(1, 0),

    '&:hover': {
      color: theme.palette.accent.main,
      textDecoration: 'none',
    }
  }
}))

const NavLink = styled(HeaderLink)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'none',

    [`${NavText}`]: {
      borderColor: '#ffd0ec',
    }
  }
}))

const ProductCatalogNavIcon = styled(StyleIcon)(({ theme }) => ({
  color: theme.palette.accent.main,
  fontSize: '1.1rem',
  marginRight: theme.spacing(0.75),
}))

const ProductCatalogNavText = styled(NavText)(({ theme }) => ({
  color: theme.palette.accent.main,
  fontWeight: 600,
}))

const ProductCatalogNavLink = styled(NavLink, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active?: boolean }>(({ active, theme }) => ({
  backgroundColor: active === true ? alpha(theme.palette.accent.main, 0.07) : undefined,
  height: '100%',
  padding: theme.spacing(0, 2),
}))

const NavItem = styled('span')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',

  '&:hover': {
    [`${NavText}`]: {
      borderColor: '#ffd0ec',
    },
    [`${Subnav}`]: {
      display: 'block',
    }
  }
}))

const ProductCatalogNavItem = styled(NavItem)(() => ({
  padding: 0,

  '&:hover': {
    [`${ProductCatalogNavText}`]: {
      borderColor: '#ffd0ec',
    },
  }
}))

interface InfoNavItem {
  url: string
  name: string
  reloadDocument?: boolean
}

export default function DesktopNav() {
  const [t] = useTranslation('header')
  const [tR] = useTranslation('routes')
  const location = useLocation()

  const currency = useAppSelector(state => state.get('userData').get('prefs').get('currency'));
  const lang = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const helpCenterUrl = getHelpCenterUrl(lang);
  const viewCategories = useAppSelector(state => state.get('appData').get('viewCategories'))
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'))

  const [prefDialogOpened, setPrefDialogOpened] = useState(false);

  const preferencesLabel = t('{{lang}} • ${{currency}}', {
    lang: lang === 'fr_CA' ? 'FR' : 'EN',
    currency,
  })

  const productCatalogPath = tR('/products').replace(/\/+$/, '') || '/'
  const currentPath = location.pathname.replace(/\/+$/, '') || '/'
  const productCatalogActive = currentPath === productCatalogPath || currentPath.startsWith(productCatalogPath + '/')

  const onPrefDialogOpen = useCallback(() => {
    setPrefDialogOpened(true)
  }, [])

  const onPrefDialogClose = useCallback(() => {
    setPrefDialogOpened(false)
  }, [])

  const infoNav = useMemo(() => {
    return [
      {
        'slug': 'general',
        'name': t('General'),
        'items': [
          {
            'url': tR('/drop-shipping'),
            'name': t('What is Drop Shipping'),
            'reloadDocument': true,
          },
          {
            'url': tR('/wholesale'),
            'name': t('Wholesale'),
            'reloadDocument': true,
          },
          {
            'url': tR('/custom-labels-and-packaging'),
            'name': t('Custom Labels & Packaging'),
            'reloadDocument': true,
          },
          {
            'url': tR('/info/sample-packs'),
            'name': t('Order sample packs'),
          },
        ],
      },
      {
        'slug': 'general2',
        'name': ' ',
        'items': [
          {
            'url': tR('/info/production-and-shipping'),
            'name': t('Production and Shipping'),
            'reloadDocument': true,
          },
          {
            'url': tR('/info/pricing'),
            'name': t('Pricing'),
            'reloadDocument': true,
          },
          {
            'url': tR('/info/design-guidelines'),
            'name': t('Design Guidelines'),
            'reloadDocument': true,
          },
        ],
      },
      {
        'slug': 'integrations',
        'name': t('Integrations'),
        'items': [
          {
            'url': tR('/drop-shipping/etsy'),
            'name': t('Etsy'),
            'reloadDocument': true,
          },
          {
            'url': tR('/drop-shipping/shopify'),
            'name': t('Shopify'),
            'reloadDocument': true,
          },
          {
            'url': tR('/drop-shipping/squarespace'),
            'name': t('Squarespace'),
            'reloadDocument': true,
          },
          {
            'url': tR('/drop-shipping/bigcartel'),
            'name': t('Big Cartel'),
            'reloadDocument': true,
          },
        ],
      },
      {
        'slug': 'policies',
        'name': t('Policies'),
        'items': [
          {
            'url': tR('/info/return-policy'),
            'name': t('Refund/Return'),
            'reloadDocument': true,
          },
          {
            'url': tR('/info/privacy-policy'),
            'name': t('Privacy'),
            'reloadDocument': true,
          },
          {
            'url': tR('/info/terms-of-service'),
            'name': t('Terms of Service'),
            'reloadDocument': true,
          },
        ],
      },
    ];
  }, [t, tR])

  const renderSubnavItem = useCallback((item: InfoNavItem) => {
    return <SubnavLi key={item.name}>
      <HeaderLink to={item.url} reloadDocument={item.reloadDocument}>{item.name}</HeaderLink>
    </SubnavLi>
  }, [])

  const renderSubnavSection = useCallback((section:{ name: string, items: InfoNavItem[]}) => {
    return <SubnavSection key={section.name}>
      <Typography variant="h4" sx={{height: '25px'}}>{section.name}</Typography>
      <SubnavUl>
        { section.items.map(renderSubnavItem) }
      </SubnavUl>
    </SubnavSection>
  }, [renderSubnavItem])

  return <Box
    component="div"
    sx={{
      backgroundColor: '#fff',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      display: {
        xs: 'none',
        md: 'block',
      },
      position: 'relative',
      zIndex: 2,
    }}
  >
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        position: 'relative',
        px: {
          xs: 0,
          md: 1,
        }
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          height: '52px',
        }}
      >
        <Stack direction="row" alignItems="stretch">
          <ProductCatalogNavItem>
            <ProductCatalogNavLink
              to={tR('/products')}
              active={productCatalogActive}
              aria-current={productCatalogActive ? 'page' : undefined}
            >
              <ProductCatalogNavIcon />
              <ProductCatalogNavText>{t('Product Catalog')}</ProductCatalogNavText>
            </ProductCatalogNavLink>
          </ProductCatalogNavItem>
          <NavItem>
            <NavText>{t('Infos')}</NavText>
            <Subnav>
              <SubnavBody direction="row" spacing={6}>
                {infoNav.map(renderSubnavSection)}
              </SubnavBody>
            </Subnav>
          </NavItem>
          <NavLink to={helpCenterUrl} target="_blank" rel="noopener" reloadDocument>
            <NavText>{t('Help')}</NavText>
          </NavLink>
          <NavLink to="https://blog.artofwhere.com" target="_blank" rel="noopener" reloadDocument>
            <NavText>{t('Blog')}</NavText>
          </NavLink>
        </Stack>
        <Button
          variant="text"
          color="secondary"
          onClick={onPrefDialogOpen}
          startIcon={<PublicRoundedIcon />}
          sx={{
            display: 'flex',
            height: '100%',
            fontSize: '.9rem',
            fontWeight: 400,
            borderRadius: '0',
            alignItems: 'center',
          }}
        >{preferencesLabel}</Button>
      </Stack>
    </Container>

    <PreferencesDialog
      opened={prefDialogOpened}
      onClose={onPrefDialogClose}
    />
  </Box>
}
